import React, {useEffect, useState} from 'react';
import {
    AccentWithDestructive,
    Alert,
    Button,
    ColumnDataType,
    DataTable,
    Spinner,
    SquareButton,
} from "@tag/tag-components-react-v4";
import AppGroupService from "../../APP/services/AppGroupService";
import {CircleInfoFilled, CrosshairFilled, MergeFilled} from "@tag/tag-icons";
import {AppGroupOctopusSync} from "../../APP/types/AppGroupOctopusSync";
import {OctopusSyncAction} from "../../APP/types/OctopusSyncAction";
import APPOctoSyncDetailsModal from "./APPOctoSyncDetailsModal";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import AppRolesService from "../../General/services/AppRolesService";
import styles from './APPOctoSync.module.scss';
import APPOctoSyncAllModal from "./APPOctoSyncAllModal";
import {ProjectAccents} from "../../General/config/projectAccents";

const APPOctoSync = () => {
    const [appGroups, setAppGroups] = useState<AppGroupOctopusSync[]>([]);
    const [loading, setLoading] = useState<boolean | string>();
    const [syncAllModalVisible, setSyncAllModalVisible] = useState<boolean>(false);
    const [alert, setAlert] = useState<{type: any, message: string}>();
    const [details, setDetails] = useState<{heading: string, actions: OctopusSyncAction[]}>();
    const hasMultipleCorporateSites = new Set(
        appGroups
            .map(appGroup => appGroup.corporateSiteName)
            .filter((siteName): siteName is string => !!siteName)
    ).size > 1;

    const { accounts, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    while (!inProgress) {
        if (!AppRolesService.isUserAdmin(accounts, isAuthenticated)) {
            throw new Error()
        }
    }
    
    useEffect(() => {
        setLoading(true);
        AppGroupService.getAppGroups()
            .then((response) => {
                setAppGroups(response)
                setLoading(false);
            });
    }, []);
    
    const performAppGrpSync = async (appGroup: string, corporateSiteName?: string) => {
        setLoading(`Starting the sync of ${appGroup}...`);

        try {
            const task = await AppGroupService.octopusSyncForAppGroup(appGroup, corporateSiteName);
            const result = await AppGroupService.waitForOctopusSync(task.taskId, message => setLoading(message));

            setAppGroups(currentAppGroups => currentAppGroups.map(ag => {
                if (ag.name === appGroup && ag.corporateSiteName === corporateSiteName) {
                    return {
                        ...ag,
                        updated: result?.updatedTenant ?? 0,
                        created: result?.createdTenant ?? 0,
                        deleted: result?.deletedTenant ?? 0,
                        actions: result?.actions ?? [],
                    };
                }
                return ag;
            }));
        }
        catch {
            setAlert({type: 'error', message: 'There was an issue with the sync, please investigate'});
        }
        finally {
            setLoading(false);
        }
    }

    const applySyncAllResult = (result: any) => {
        const resultsByAppGroup = new Map<string, any>(
            (result?.appGroups ?? []).map((appGroup: any) => [appGroup.appGroup, appGroup])
        );

        setAppGroups(currentAppGroups => currentAppGroups.map(ag => {
            const resultForAppGroup = resultsByAppGroup.get(ag.name);

            return {
                ...ag,
                deleted: resultForAppGroup?.deletedTenant ?? 0,
                updated: resultForAppGroup?.updatedTenant ?? 0,
                created: resultForAppGroup?.createdTenant ?? 0,
                actions: resultForAppGroup?.actions,
            };
        }));

        setDetails({
            heading: 'Sync All - what the sync did',
            actions: result?.actions ?? []
        });
    }
    
    const getData = () => {
        let shaded = false;
        return appGroups.map((ag) => {
            shaded = !shaded;
            
            return {
                appGroup: ag.name,
                corporateSite: ag.corporateSiteName,
                shaded: shaded,
                deletedTenantCount: ag.deleted,
                updatedTenantCount: ag.updated,
                createdTenantCount: ag.created,
                actions: ag.actions
            }
        });
    }

    const renderSyncButton = ({row}: any) => {
            return (
                <Button
                    icon={<MergeFilled />}
                    style={{backgroundColor: '#aee9e9'}}
                    onClick={() => {
                        performAppGrpSync(row.original.appGroup, row.original.corporateSite);
                    }}
                />
            )
        }

    const renderDetailsButton = ({row}: any) => {
        const actions: OctopusSyncAction[] | undefined = row.original.actions;
        if (!actions) {
            return null;
        }

        return (
            <SquareButton
                accent={ProjectAccents.PeoplePlanner as AccentWithDestructive}
                icon={<CircleInfoFilled />}
                onClick={() => setDetails({
                    heading: `${row.original.appGroup} - what the sync did`,
                    actions: actions
                })}
            />
        );
    }

    const getColumns = () => {
        const columns = [
            { accessorKey: 'appGroup', header: 'App Group', meta: { dataType: 'string' as ColumnDataType } },
            { accessorKey: 'sync', header: 'Sync', cell: renderSyncButton },
            { accessorKey: 'deletedTenantCount', header: 'Deleted', meta: { dataType: 'integer' as ColumnDataType } },
            { accessorKey: 'updatedTenantCount', header: 'Updated', meta: { dataType: 'integer' as ColumnDataType } },
            { accessorKey: 'createdTenantCount', header: 'Created', meta: { dataType: 'integer' as ColumnDataType } },
            { accessorKey: 'details', header: 'Details', cell: renderDetailsButton },
        ]

        if (hasMultipleCorporateSites) {
            columns.unshift({
                accessorKey: 'corporateSite',
                header: 'Corporate Site',
                meta: { dataType: 'string' as ColumnDataType }
            });
        }

        return columns;
    }

    let header = (
        <div style={{marginLeft: '2em'}}>
            <Button
                className={styles.OctopusSyncBackButton}
                onClick={() => setSyncAllModalVisible(true)}
                icon={<CrosshairFilled />}
            >Sync All</Button>
            <h1 className={styles.OctopusSyncHeader}>Octopus Sync</h1>
        </div>
    )
    let content = null;
    if (loading) {
        content = <Spinner full>{loading}</Spinner>;
    }
    else {
        content = (
            <DataTable
                scrollerProps={{
                    style:{ width: 'unset' } // Temporary while scroller not needed
                }}
                getRowProps={(row) => ({
                    style: {
                        backgroundColor: row.original.shaded ? '#FEFAEC' : '#F3F1FA'
                    },
                })}
                enableSorting={true}
                columns={getColumns()}
                data={getData()}
            />
        );
    }

    return (
        <>
            {header}
            {content}
            {syncAllModalVisible ?
                <APPOctoSyncAllModal
                    visible={syncAllModalVisible}
                    handleClose={() => setSyncAllModalVisible(false)}
                    setAlert={setAlert}
                    setLoading={setLoading}
                    onSyncAllComplete={applySyncAllResult}
                /> : undefined
            }
            {details ?
                <APPOctoSyncDetailsModal
                    visible={true}
                    heading={details.heading}
                    actions={details.actions}
                    handleClose={() => setDetails(undefined)}
                /> : undefined
            }
            {alert ?
                <Alert
                    className={styles.CustomerListAlert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={1200}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }
        </>
    );
}

export default APPOctoSync;
